const BASE_DOMAINS = [
	"tlmwholesale.myshopify.com",
	"tlmwholesale.com",
	"store.tlmwholesale.com",
];
const INTERNAL_DOMAINS = [
	...BASE_DOMAINS,
	...BASE_DOMAINS.map((domain) => `https://${domain}`),
];

function maybeTruncate(text, limit, noEllipsis) {
	if (text.length < limit) return text;

	let buffer = 0;
	let truncated = text.slice(0, limit);
	const lastSpace = truncated.lastIndexOf(" ");
	const finalChar = text.charAt(lastSpace - 1);
	const punctuation = [
		",",
		".",
		"!",
		"?",
		";",
		":",
		"-",
		"—",
		"–",
		"(",
		")",
		"[",
		"]",
		"{",
		"}",
		'"',
		"'",
		"“",
		"”",
		"‘",
		"’",
	];

	if (punctuation.includes(finalChar)) {
		buffer = 1;
	}

	truncated = text.slice(0, lastSpace - buffer) + (noEllipsis ? "" : "...");

	return truncated;
}

/**
 * Validates that a url is local
 * @param url
 * @returns `true` if local `false`if external domain
 */
function isLocalPath(url) {
	try {
		// We don't want to redirect cross domain,
		// doing so could create fishing vulnerability
		// If `new URL()` succeeds, it's a fully qualified
		// url which is cross domain. If it fails, it's just
		// a path, which will be the current domain.
		new URL(url);
	} catch (e) {
		return true;
	}

	return false;
}

function isInternalUrl(url) {
	if (isLocalPath(url)) return true;

	try {
		const { hostname } = new URL(url);
		return !!INTERNAL_DOMAINS.some(
			(domain) => hostname === domain || hostname.endsWith(`.${domain}`),
		);
	} catch {
		return true;
	}
}

function externalLinkHandler(anchorLink) {
	const url = anchorLink.getAttribute("href") || anchorLink.getAttribute("to");

	let parsedLink = {
		isExternal: false,
		to: url ?? "/",
		target: "_self",
	};

	if (!url) {
		return parsedLink;
	}

	const isInternalLink = isInternalUrl(url);
	let existingBlank = false;
	let pathname = url;

	if (isInternalLink) {
		if (!isLocalPath(url)) {
			try {
				const relative = new URL(url);
				pathname = relative.pathname + relative.search + relative.hash;
			} catch {
				pathname = url;
			}
		}
	} else {
		pathname = url;
	}

	const cleanedPath = pathname.trim().replace(/https?:\/\//, "");

	existingBlank = anchorLink.getAttribute("target") === "_blank";
	if (isInternalLink) {
		if (existingBlank || pathname.includes("account.tlmwholesale")) {
			parsedLink = {
				isExternal: false,
				target: "_blank",
				to: pathname,
				rel: "noopener",
				ariaLabel: anchorLink.textContent
					? `${anchorLink.textContent.trim()}, (Link opens in a new tab)`
					: `Links to ${cleanedPath}, (opens in a new tab)`,
			};
		} else {
			parsedLink = {
				isExternal: false,
				target: "_self",
				to: pathname,
			};
		}
	} else {
		parsedLink = {
			isExternal: true,
			target: "_blank",
			to: pathname,
			rel: "noopener noreferrer",
			ariaLabel: anchorLink.textContent
				? `${anchorLink.textContent.trim()}, (External link opens in a new tab)`
				: `Links to ${cleanedPath}, (External link opens in a new tab)`,
		};
	}
	return parsedLink;
}

function isEmptyHeading(heading) {
	return !!(
		!heading.innerText ||
		heading.innerText.trim() === "" ||
		!heading.innerHTML
	);
}

function replaceHeading(headingEl, newEl) {
	newEl.innerHTML = headingEl.innerHTML;
	for (const attr of headingEl.attributes) {
		newEl.setAttribute(attr.name, attr.value);
	}
	headingEl.replaceWith(newEl);
}

function ADASanitizeLinkTitle(link) {
	const linkText = link.textContent?.trim() ?? null;
	const title = link.getAttribute("title")?.trim() ?? null;
	const hasImage = !!link.querySelector("img") || !!link.querySelector("i");
	const isIconImg = hasImage && !!link.querySelector("i");

	if ((!linkText && !title) || (linkText === "" && title === "")) {
		return link;
	}

	if (linkText && title) {
		link.removeAttribute("title");
	} else {
		if (!linkText && title) {
			if (!isIconImg) {
				if (!hasImage) {
					link.innerText = title;
				}
				link.removeAttribute("title");
			}
		}
	}
	return link;
}

function ADASanitizeAltText(imageSrc) {
	let altText = "";
	const source =
		typeof imageSrc === "string"
			? imageSrc
			: (imageSrc.getAttribute("src") ??
				imageSrc.getAttribute("data-src") ??
				imageSrc.getAttribute("data-sanitized-data-src"));
	if (!source) return "";

	const hasAlt = typeof imageSrc !== "string" && imageSrc.hasAttribute("alt");

	if (hasAlt)
		return maybeTruncate(imageSrc.getAttribute("alt") || "", 124, true);

	const filename = source.split("/").pop()?.split(".")[0] || "";
	const parts = filename.split(/[-_]/).filter((part) => {
		if (/^\d+$/.test(part)) return false;
		if (/^[a-f]+$/.test(part) && (part.includes("a") || part.includes("e")))
			return true;
		if (/^[a-f0-9]+$/.test(part)) return false;
		return true;
	});

	altText = parts.join(" ").replace(/\s+/g, " ").toLowerCase().trim();
	return maybeTruncate(altText, 124, true) || "";
}

function ADASanitizeDescriptionHtml(htmlString) {
	if (!htmlString) return "";

	if (typeof DOMParser === "undefined") return htmlString;

	const parser = new DOMParser();
	const doc = parser.parseFromString(htmlString, "text/html");

	const headings = doc.querySelectorAll("h1, h2, h3, h4, h5, h6");
	const nonEmptyH1Data = Array.from(headings).filter(
		(h) => h.tagName.toLowerCase() === "h1" && !isEmptyHeading(h),
	);

	headings.forEach((heading) => {
		const isEmpty = isEmptyHeading(heading);
		if (isEmpty) {
			const newDiv = document.createElement("div");
			replaceHeading(heading, newDiv);
			return;
		}

		if (nonEmptyH1Data.length) {
			const level = parseInt(heading.tagName.substring(1));
			if (!isEmpty && level < 6) {
				const newHeading = doc.createElement(`h${level + 1}`);
				replaceHeading(heading, newHeading);
			}
		}
	});

	const underlines = doc.querySelectorAll(
		".underline, [style*='text-decoration: underline']",
	);
	underlines.forEach((el) => {
		if (el.hasAttribute("href") || el.tagName.toLowerCase() === "a") return;
		el.classList.remove("underline");
		el.style.textDecoration = el.style.textDecoration.replace(/underline/g, "");
	});

	// Fix images missing alt attributes
	const images = doc.querySelectorAll("img");
	images.forEach((img) => {
		img.setAttribute("alt", ADASanitizeAltText(img));
	});

	// Fix images where alt duplicates adjacent text
	const imgLinks = doc.querySelectorAll("a");
	imgLinks.forEach((link) => {
		const img = link.querySelector("img");
		if (!img) return;

		const alt = (img.getAttribute("alt") || "").toLowerCase().trim();
		if (!alt) return;

		// Get all text content from the link, excluding img alt attributes
		const clone = link.cloneNode(true);
		clone.querySelectorAll("img").forEach((i) => i.remove());
		const linkText = clone.textContent?.toLowerCase().trim() || "";

		if (linkText) {
			img.setAttribute("alt", "");
		}
	});

	// Fix layout tables - convert to divs
	const tables = doc.querySelectorAll("table");
	tables.forEach((table) => {
		const hasHeaders = table.querySelector("thead, th");
		if (!hasHeaders) {
			const wrapper = doc.createElement("div");
			wrapper.className = "ada-cleaned-table-body";

			const rows = table.querySelectorAll("tr");
			rows.forEach((row) => {
				const rowDiv = doc.createElement("div");
				rowDiv.className = "ada-cleaned-table-row";

				const cells = row.querySelectorAll("td");
				cells.forEach((cell) => {
					const cellDiv = doc.createElement("div");
					cellDiv.innerHTML = cell.innerHTML;
					if (cell.style.width) {
						cellDiv.style.width = cell.style.width;
					}
					rowDiv.appendChild(cellDiv);
				});

				wrapper.appendChild(rowDiv);
			});

			table.replaceWith(wrapper);
		}
	});

	// Fix links missing accessible text
	const allLinks = doc.querySelectorAll("a");
	allLinks.forEach((link) => {
		const rawHref = link.getAttribute("href");

		if (
			!rawHref ||
			rawHref === "" ||
			rawHref === "#" ||
			rawHref.startsWith("#") ||
			rawHref.startsWith("mailto:") ||
			rawHref.startsWith("tel:")
		)
			return;

		link = ADASanitizeLinkTitle(link);

		const parsed = externalLinkHandler(link);

		link.setAttribute("target", parsed.target);

		if (parsed.rel && parsed.rel !== "") {
			link.setAttribute("rel", parsed.rel);
		} else {
			link.removeAttribute("rel");
		}

		if (!parsed.isExternal && parsed.to && !parsed.to.startsWith("/")) {
			try {
				const { pathname } = new URL(parsed.to);
				link.setAttribute("href", pathname);
			} catch {}
		}

		const cleanedHref = rawHref.trim().replace(/https?:\/\//, "");
		const innerText = link.textContent?.trim();
		const img = link.querySelector("img") || link.querySelector("i");
		const hasAriaLabel = !!(
			link.hasAttribute("aria-label") &&
			link.getAttribute("aria-label")?.trim() !== ""
		);
		const hasAlt = !!(
			img &&
			img.hasAttribute("alt") &&
			img.getAttribute("alt")?.trim() !== ""
		);
		const hasInnerText = !!(innerText && innerText !== "");
		const isIconImg = !!(img && img.tagName.toLowerCase() === "i");
		const isImgOnlyLink = !!(img && !hasInnerText);
		const existingLabel = link.getAttribute("aria-label")?.trim() || "";

		let appendedLabel = "";
		let altText = "";

		if (parsed.isExternal) {
			if (hasInnerText) {
				if (!innerText.includes("opens in a new tab")) {
					appendedLabel = " (External link opens in a new tab)";
				} else {
					appendedLabel = "";
				}
			} else {
				appendedLabel = ` Links to ${cleanedHref}, (External link opens in a new tab)`;
			}
		} else {
			if (parsed.target !== "_blank") {
				if (!hasInnerText) {
					if (rawHref.includes("/cdn/")) {
						appendedLabel = " Opens Image Preview";
					} else {
						appendedLabel = ` Links to ${cleanedHref}`;
					}
				} else {
					appendedLabel = "";
				}
			} else {
				if (hasInnerText) {
					if (!innerText.includes("opens in a new tab")) {
						appendedLabel = " (opens in a new tab)";
					} else {
						appendedLabel = "";
					}
				} else {
					if (rawHref.includes("/cdn/")) {
						appendedLabel = " Image Preview, (opens in a new tab)";
					} else {
						appendedLabel = ` Links to ${cleanedHref}, (opens in a new tab)`;
					}
				}
			}
		}

		if (img) {
			if (isIconImg) {
				if (img.hasAttribute("alt")) {
					img.removeAttribute("alt");
				}
				img.setAttribute("aria-hidden", "true");
			} else {
				if (hasAlt) {
					altText = img.getAttribute("alt").trim();
				} else {
					altText = ADASanitizeAltText(img);
				}
			}
		}

		if (isImgOnlyLink) {
			if (!hasAriaLabel) {
				if (!isIconImg) {
					if (hasAlt) {
						if (!altText.includes("Links to")) {
							img.setAttribute("alt", `${altText}, ${appendedLabel}`);
						} else {
							img.setAttribute("alt", altText);
						}
					} else if (parsed.ariaLabel && parsed.ariaLabel !== "") {
						if (!parsed.ariaLabel.includes("Links to")) {
							img.setAttribute("alt", `${parsed.ariaLabel}, ${appendedLabel}`);
						} else {
							img.setAttribute("alt", parsed.ariaLabel);
						}
					} else {
						img.setAttribute("alt", appendedLabel);
					}
				} else {
					if (parsed.ariaLabel && parsed.ariaLabel !== "") {
						if (!parsed.ariaLabel.includes("Links to")) {
							link.setAttribute(
								"aria-label",
								`${parsed.ariaLabel}, ${appendedLabel}`,
							);
						} else {
							link.setAttribute("aria-label", parsed.ariaLabel);
						}
					} else {
						link.setAttribute("aria-label", appendedLabel);
					}
				}
			} else if (existingLabel && existingLabel !== "") {
				if (!isIconImg) {
					if (!existingLabel.includes("Links to")) {
						img.setAttribute("alt", `${existingLabel}, ${appendedLabel}`);
					} else {
						img.setAttribute("alt", existingLabel);
					}
					link.removeAttribute("aria-label");
				} else {
					if (!existingLabel.includes("Links to")) {
						link.setAttribute(
							"aria-label",
							`${existingLabel}, ${appendedLabel}`,
						);
					} else {
						link.setAttribute("aria-label", existingLabel);
					}
				}
			} else {
				if (!isIconImg) {
					img.setAttribute("alt", appendedLabel);
				} else {
					link.setAttribute("aria-label", appendedLabel);
				}
			}
		} else {
			if (parsed.target === "_blank") {
				if (hasAriaLabel) {
					if (hasInnerText) {
						link.removeAttribute("aria-label");
						if (!innerText.includes("opens in a new tab")) {
							const srOnly = doc.createElement("span");
							srOnly.className = "sr-only";
							srOnly.textContent = appendedLabel;
							link.appendChild(srOnly);
						}
					} else {
						if (existingLabel && existingLabel !== "") {
							if (!existingLabel.includes("opens in a new tab")) {
								link.setAttribute(
									"aria-label",
									`${existingLabel}, ${appendedLabel}`,
								);
							} else {
								link.setAttribute("aria-label", existingLabel);
							}
						}
					}
				} else if (hasInnerText && !innerText.includes("opens in a new tab")) {
					const srOnly = doc.createElement("span");
					srOnly.className = "sr-only";
					srOnly.textContent = appendedLabel;
					link.appendChild(srOnly);
				} else if (parsed.ariaLabel && parsed.ariaLabel !== "") {
					if (!parsed.ariaLabel.includes("opens in a new tab")) {
						link.setAttribute(
							"aria-label",
							`${parsed.ariaLabel}, ${appendedLabel}`,
						);
					} else {
						link.setAttribute("aria-label", parsed.ariaLabel);
					}
				} else {
					if (!hasInnerText && !hasAriaLabel) {
						if (img && !isImgOnlyLink) {
							if (!isIconImg) {
								if (hasAlt) {
									if (!altText.includes("opens in a new tab")) {
										img.setAttribute("alt", `${altText}, ${appendedLabel}`);
									} else {
										img.setAttribute("alt", altText);
									}
								}
							} else {
								link.setAttribute("aria-label", appendedLabel);
							}
						} else {
							link.setAttribute("aria-label", appendedLabel);
						}
					}
				}
			}
		}

		if (img) {
			if (hasAlt) {
				img.setAttribute(
					"alt",
					maybeTruncate(img.getAttribute("alt"), 124, true),
				);
			} else if (img.hasAttribute("aria-label")) {
				img.setAttribute(
					"aria-label",
					maybeTruncate(img.getAttribute("aria-label"), 149, true),
				);
			}
		} else if (hasAriaLabel) {
			link.setAttribute(
				"aria-label",
				maybeTruncate(link.getAttribute("aria-label"), 149, true),
			);
		}
	});

	return doc.body.innerHTML;
}

document.addEventListener("DOMContentLoaded", function () {
	Array.from(
		document.getElementsByClassName("sanitize-description-html"),
	).forEach((element) => {
		let html = "";
		Array.from(element.childNodes).forEach((node) => {
			html +=
				node.nodeType === Node.ELEMENT_NODE ? node.outerHTML : node.textContent;
		});
		element.innerHTML = ADASanitizeDescriptionHtml(html);
	});

	Array.from(document.getElementsByClassName("sanitize-alt-text")).forEach(
		(element) => {
			if (element.tagName.toLowerCase() === "img") {
				const alt = ADASanitizeAltText(element);
				element.setAttribute("alt", alt);
			}
		},
	);

	Array.from(document.getElementsByClassName("sanitize-link-title")).forEach(
		(element) => {
			if (element.tagName.toLowerCase() === "a") {
				ADASanitizeLinkTitle(element);
			}
		},
	);

	// used for dynamically loaded content that needs to be reloaded and sanitized, such as the featured products grid
	document.dispatchEvent(new Event("afterSanitization"));
	// Lets a listener registered AFTER this point (e.g. a script whose own
	// DOMContentLoaded handler happens to run before this one) detect that
	// sanitization already completed, instead of waiting on an event that
	// already fired and will never fire again this page load.
	window.__adaSanitizationDone = true;
});

// Expose functions to global scope for use in other scripts
window.ADASanitizeDescriptionHtml = ADASanitizeDescriptionHtml;
window.ADASanitizeAltText = ADASanitizeAltText;
window.ADASanitizeLinkTitle = ADASanitizeLinkTitle;
window.externalLinkHandler = externalLinkHandler;
window.maybeTruncate = maybeTruncate;
window.isInternalUrl = isInternalUrl;
window.isLocalPath = isLocalPath;
